import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getBooking, getAllJournalEntries, submitTestimonial } from '../utils/api';
import { C, TESTIMONIAL_QUESTIONS, TESTIMONIAL_FEEDBACK_QUESTION } from '../utils/constants';

// Share URL — opt-in page entry point to the free masterclass funnel.
//
// ⚠ TAGGED, AND EVERY USE BELOW MUST USE THIS CONSTANT. Was the bare
// https://go.benmilston.com until 2026-09-07, which meant every friend a
// graduate sent it to arrived with no UTMs and was reported on the admin
// Traffic tab as "Direct" — traffic Ben was generating himself and could not
// see. There are THREE uses in this file (the on-screen text, the clipboard
// copy, and the WhatsApp message) and all three must carry the tags: a client
// who selects the text by hand and one who taps the WhatsApp button are the
// same visit and must land on the same row.
//
// The tags MUST stay byte-identical to the 'RY Course — Testimonial' spec in
// backend/src/config/adNames.js — that spec is what turns these arrivals into a
// named row instead of 'Other (untracked)', and it matches on all four fields.
// Change one without the other and the row silently stops counting.
const MASTERCLASS_URL =
  'https://go.benmilston.com/?utm_source=ry-course&utm_medium=referral&utm_campaign=funnel&utm_content=testimonial';

const CONFETTI_COLORS = ['#F5A623', '#E8830A', '#AD795B', '#6B3E0A', '#fff', '#FFF8F7', '#f0c060'];

const Confetti = () => {
  const pieces = useMemo(() => Array.from({ length: 70 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 4,
    duration: 3 + Math.random() * 3,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    size: 7 + Math.random() * 8,
    isCircle: Math.random() > 0.5,
  })), []);

  return (
    <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden', zIndex: 0 }}>
      {pieces.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          left: `${p.x}%`,
          top: -20,
          width: p.size,
          height: p.size,
          background: p.color,
          borderRadius: p.isCircle ? '50%' : 2,
          animation: `confettiFall ${p.duration}s ${p.delay}s ease-in infinite, confettiSway ${p.duration * 0.6}s ${p.delay}s ease-in-out infinite`,
          opacity: 0.85,
        }} />
      ))}
    </div>
  );
};

const StarRating = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 10, justifyContent: 'center', margin: '8px 0 20px' }}>
    {[1, 2, 3, 4, 5].map(star => (
      <button
        key={star}
        onClick={() => onChange(star)}
        style={{
          background: 'none',
          border: 'none',
          fontSize: '2.2rem',
          cursor: 'pointer',
          color: star <= value ? C.gold : '#ddd',
          transition: 'color 0.15s, transform 0.15s',
          transform: star <= value ? 'scale(1.15)' : 'scale(1)',
          padding: '0 2px',
        }}
        aria-label={`${star} star`}
      >
        ★
      </button>
    ))}
  </div>
);

export const CompletionFlow = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [booking, setBooking] = useState(null);
  const [journalCount, setJournalCount] = useState(0);
  const [countdown, setCountdown] = useState('');
  const [feedbackStars, setFeedbackStars] = useState([0, 0, 0, 0, 0]); // 5 separate ratings
  const [feedbackText, setFeedbackText] = useState('');                 // optional
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);

  const firstName = user?.firstName || user?.first_name || 'Friend';

  useEffect(() => {
    getBooking().then(r => setBooking(r.data)).catch(() => {});
    getAllJournalEntries().then(r => {
      const entries = r.data || [];
      setJournalCount(entries.filter(e => e.answer?.trim()).length);
    }).catch(() => {});
  }, []);

  // Session countdown
  useEffect(() => {
    if (!booking?.sessionDate) return;
    const calc = () => {
      const diff = new Date(booking.sessionDate) - new Date();
      if (diff <= 0) return 'Your session is here!';
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      return d > 0 ? `${d} day${d !== 1 ? 's' : ''}, ${h}h ${m}m` : `${h}h ${m}m`;
    };
    setCountdown(calc());
    const id = setInterval(() => setCountdown(calc()), 30000);
    return () => clearInterval(id);
  }, [booking]);

  const allStarsRated = feedbackStars.every(s => s >= 1);

  const setStar = (idx, val) => {
    setFeedbackStars(prev => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  };

  const handleTestimonialSubmit = async () => {
    if (!allStarsRated) return;
    setSubmitting(true);
    try {
      await submitTestimonial({
        star1:        feedbackStars[0],
        star2:        feedbackStars[1],
        star3:        feedbackStars[2],
        star4:        feedbackStars[3],
        star5:        feedbackStars[4],
        feedbackText: feedbackText.trim() || null,
      });
    } catch (e) {}
    setSubmitted(true);
    setSubmitting(false);
    setStep(3);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(MASTERCLASS_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const card = {
    background: '#fff',
    borderRadius: 18,
    padding: '36px 32px',
    boxShadow: '0 8px 40px rgba(61,31,2,0.12)',
    width: '100%',
    maxWidth: 520,
    position: 'relative',
    zIndex: 1,
  };

  // ─── STEP 0: CELEBRATION ───────────────────────────────────────────────────
  if (step === 0) return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(180deg, #1a0800 0%, #3d1f02 55%, #6B3E0A 100%)`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <Confetti />

      <div style={{ ...card, textAlign: 'center' }}>
        {/* Trophy */}
        <div style={{ fontSize: '4rem', marginBottom: 8 }}>🏆</div>

        <h1 style={{
          fontFamily: "'Forum', serif",
          color: C.brown,
          fontSize: 'clamp(1.6rem, 5vw, 2.2rem)',
          letterSpacing: '0.03em',
          marginBottom: 20,
          lineHeight: 1.3,
        }}>
          {firstName}, you made it.
        </h1>

        {/* Personal message */}
        <div style={{
          background: C.veryLightBrown,
          borderRadius: 12,
          padding: '20px 24px',
          marginBottom: 24,
          borderLeft: `4px solid ${C.gold}`,
          textAlign: 'left',
        }}>
          <p style={{
            fontFamily: "'Forum', serif",
            color: C.brown,
            fontSize: '1rem',
            lineHeight: 1.75,
            fontStyle: 'italic',
            margin: 0,
          }}>
            "Well done! You have completed this course, but have begun a journey far more important.
          </p>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            margin: '12px 0',
            paddingLeft: 4,
          }}>
            {[
              'A journey of rediscovery.',
              'A journey of reconnecting.',
              'A journey of reconditioning.',
              'A journey of realigning.',
            ].map(line => (
              <div key={line} style={{
                fontFamily: "'Forum', serif",
                // Dark red (deliberately local literal — only these four
                // lines change; the rest of the page keeps using C.gold).
                color: '#B83418',
                fontSize: '1rem',
                lineHeight: 1.6,
                fontStyle: 'italic',
                letterSpacing: '0.02em',
              }}>
                {line}
              </div>
            ))}
          </div>
          <p style={{
            fontFamily: "'Forum', serif",
            color: C.brown,
            fontSize: '1rem',
            lineHeight: 1.75,
            fontStyle: 'italic',
            margin: 0,
          }}>
            Welcome to the real journey of life — where you continue to walk YOUR path, for YOU."
          </p>
          <p style={{
            marginTop: 12,
            fontSize: '0.85rem',
            color: C.terracotta,
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}>— Ben</p>
        </div>

        {/* Stats */}
        <div style={{
          display: 'flex',
          gap: 16,
          justifyContent: 'center',
          marginBottom: 28,
        }}>
          {[
            { value: '6', label: 'Days Completed' },
            { value: journalCount, label: 'Journal Responses' },
          ].map(s => (
            <div key={s.label} style={{
              flex: 1,
              background: `linear-gradient(135deg, ${C.amber}, ${C.gold})`,
              borderRadius: 12,
              padding: '16px 12px',
              color: '#fff',
            }}>
              <div style={{ fontFamily: "'Forum', serif", fontSize: '2rem', fontWeight: 700 }}>{s.value}</div>
              <div style={{ fontSize: '0.75rem', opacity: 0.9, letterSpacing: '0.04em' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <button className="btn-primary" onClick={() => setStep(1)} style={{ width: '100%', fontSize: '1rem' }}>
          Continue →
        </button>
      </div>
    </div>
  );

  // ─── STEP 1: BMS REMINDER ─────────────────────────────────────────────────
  if (step === 1) return (
    <div style={{
      minHeight: '100vh',
      background: C.cream,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
    }}>
      <div style={card}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12, textAlign: 'center' }}>🗓️</div>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <p style={{
            fontFamily: "'Forum', serif",
            color: C.terracotta,
            fontSize: '0.85rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            marginBottom: 6,
          }}>
            The Next Step Is Coming Up
          </p>
          <h2 style={{
            fontFamily: "'Forum', serif",
            color: C.brown,
            fontSize: '1.7rem',
            letterSpacing: '0.03em',
          }}>
            Your Breakthrough Mapping Session
          </h2>
        </div>

        <p style={{ color: C.text, lineHeight: 1.75, marginBottom: 20, fontSize: '0.95rem' }}>
          I hope you got so much from this program. Now you have a personalised 1:1 coming up
          to help you move forward and keep this momentum alive.
        </p>
        <p style={{ color: C.text, lineHeight: 1.75, marginBottom: 24, fontSize: '0.95rem' }}>
          In your Breakthrough Mapping Session, we'll take everything you've uncovered over
          these 6 days and create a clear, personalised path forward — just for you.
        </p>

        {booking?.callBooked && booking?.sessionDate ? (
          <div style={{
            background: `linear-gradient(135deg, ${C.amber}22, ${C.gold}22)`,
            border: `2px solid ${C.gold}`,
            borderRadius: 14,
            padding: '20px 24px',
            textAlign: 'center',
            marginBottom: 24,
          }}>
            <div style={{ fontSize: '0.78rem', color: C.terracotta, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>
              Your session is in
            </div>
            <div style={{
              fontFamily: "'Forum', serif",
              color: C.amber,
              fontSize: '1.8rem',
              fontWeight: 700,
              letterSpacing: '0.04em',
            }}>
              {countdown || '—'}
            </div>
            <div style={{ fontSize: '0.82rem', color: C.brown, marginTop: 6 }}>
              {new Date(booking.sessionDate).toLocaleDateString('en-GB', {
                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
              })}
            </div>
          </div>
        ) : (
          <div style={{
            background: C.veryLightBrown,
            borderRadius: 12,
            padding: '16px 20px',
            marginBottom: 24,
            fontSize: '0.88rem',
            color: C.terracotta,
            lineHeight: 1.6,
            borderLeft: `3px solid ${C.terracotta}`,
          }}>
            Keep an eye on your email — your session details will be confirmed there.
          </div>
        )}

        <button className="btn-primary" onClick={() => setStep(2)} style={{ width: '100%', fontSize: '1rem' }}>
          Continue →
        </button>
      </div>
    </div>
  );

  // ─── STEP 2: TESTIMONIAL ──────────────────────────────────────────────────
  if (step === 2) return (
    <div style={{
      minHeight: '100vh',
      background: C.cream,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
    }}>
      <div style={{ ...card, maxWidth: 560 }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12, textAlign: 'center' }}>💬</div>
        <h2 style={{
          fontFamily: "'Forum', serif",
          color: C.brown,
          fontSize: '1.6rem',
          letterSpacing: '0.03em',
          marginBottom: 8,
          textAlign: 'center',
        }}>
          Share Your Experience
        </h2>
        <p style={{ color: C.terracotta, fontSize: '0.88rem', textAlign: 'center', marginBottom: 24, lineHeight: 1.6 }}>
          Your words help other people find the courage to start their own journey.
        </p>

        {/* Five star-rating questions */}
        {TESTIMONIAL_QUESTIONS.map((qLabel, idx) => (
          <div key={idx} style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: C.brown, letterSpacing: '0.04em', textAlign: 'center', marginBottom: 4, lineHeight: 1.5 }}>
              {qLabel}
            </label>
            <StarRating value={feedbackStars[idx]} onChange={val => setStar(idx, val)} />
          </div>
        ))}

        {/* Optional free-text question */}
        <div style={{ marginTop: 12, marginBottom: 28 }}>
          <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: C.brown, letterSpacing: '0.04em', marginBottom: 6 }}>
            {TESTIMONIAL_FEEDBACK_QUESTION}
          </label>
          <textarea
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
            placeholder="Share your thoughts... (optional)"
          />
        </div>

        <button
          className="btn-primary"
          onClick={handleTestimonialSubmit}
          disabled={submitting || !allStarsRated}
          style={{ width: '100%', fontSize: '1rem', marginBottom: 12 }}
        >
          {submitting ? 'Submitting...' : 'Submit & Continue →'}
        </button>

        <button
          onClick={() => setStep(3)}
          style={{
            width: '100%', background: 'none', border: 'none', cursor: 'pointer',
            color: C.terracotta, fontSize: '0.82rem', opacity: 0.7,
            fontFamily: "'Nunito', sans-serif", textDecoration: 'underline',
          }}
        >
          Skip for now
        </button>
      </div>
    </div>
  );

  // ─── STEP 3: REFERRAL + DASHBOARD ─────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      background: C.cream,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
    }}>
      <div style={{ ...card, textAlign: 'center' }}>
        {submitted && (
          <div style={{
            background: `${C.gold}22`,
            border: `1.5px solid ${C.gold}`,
            borderRadius: 10,
            padding: '10px 16px',
            marginBottom: 24,
            fontSize: '0.88rem',
            color: C.amber,
            fontWeight: 600,
          }}>
            ✓ Thank you — your feedback means a lot.
          </div>
        )}

        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🌟</div>
        <h2 style={{
          fontFamily: "'Forum', serif",
          color: C.brown,
          fontSize: '1.6rem',
          letterSpacing: '0.03em',
          marginBottom: 12,
        }}>
          Know Someone Who Needs This?
        </h2>
        <p style={{ color: C.text, fontSize: '0.92rem', lineHeight: 1.7, marginBottom: 24 }}>
          If this program has helped you, chances are someone in your life could benefit too.
          Share Ben's free masterclass with them — it could be exactly what they need.
        </p>

        <div style={{
          background: C.veryLightBrown,
          borderRadius: 10,
          padding: '12px 16px',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: `1.5px solid ${C.border}`,
        }}>
          <span style={{ flex: 1, fontSize: '0.82rem', color: C.brown, wordBreak: 'break-all', textAlign: 'left' }}>
            {MASTERCLASS_URL}
          </span>
          <button
            onClick={handleCopy}
            style={{
              background: copied ? C.amber : C.gold,
              border: 'none',
              borderRadius: 7,
              padding: '6px 14px',
              fontSize: '0.78rem',
              fontWeight: 700,
              color: '#fff',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background 0.2s',
            }}
          >
            {copied ? '✓ Copied' : 'Copy Link'}
          </button>
        </div>

        <a
          href={`https://wa.me/?text=${encodeURIComponent(`Hey, I just completed the Recondition Yourself 7-day program and it's been truly incredible! If you're feeling trapped in conditioning you can't seem to stop repeating, and not sure why, or what to do next to break free from it, check out Ben's free masterclass: ${MASTERCLASS_URL} Enjoy!`)}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            background: '#25D366',
            color: '#fff',
            borderRadius: 10,
            padding: '12px 20px',
            fontSize: '0.92rem',
            fontWeight: 700,
            textDecoration: 'none',
            marginBottom: 28,
            letterSpacing: '0.02em',
          }}
        >
          📲 Share via WhatsApp
        </a>

        {/* Back to Dashboard */}
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 24 }}>
          <p style={{ color: C.terracotta, fontSize: '0.88rem', marginBottom: 16, lineHeight: 1.6 }}>
            Your dashboard will now show your completed journey. You can still go back and rewatch any of the videos whenever you like.
          </p>
          <button
            className="btn-primary"
            onClick={() => navigate('/dashboard')}
            style={{ width: '100%', fontSize: '1rem' }}
          >
            Go to My Dashboard →
          </button>
        </div>
      </div>
    </div>
  );
};
